import React, { ComponentType, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActivityIcon, ChevronLeftIcon, ChevronRightIcon, GlobeIcon, HeartIcon, HeartPulseIcon, LayoutGridIcon, LogOutIcon, PackageIcon, PillIcon, ShoppingBagIcon, SparklesIcon, StethoscopeIcon, TagIcon, ThermometerIcon, TrendingUpIcon, UserIcon, BrainIcon } from 'lucide-react';
import { AuthBottomSheet } from '../components/modals/AuthBottomSheet';
import { LanguageBottomSheet } from '../components/modals/LanguageBottomSheet';
import { LoyaltyOnboardingBottomSheet } from '../components/modals/LoyaltyOnboardingBottomSheet';
import { LoyaltyCard } from '../components/account/LoyaltyCard';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { useStore } from '../lib/store';
interface MenuDestination {
  label: string;
  path: string;
}
export function MobileMenuPage() {
  const navigate = useNavigate();
  const {
    language,
    setLanguage,
    wishlist
  } = useStore();
  const [authOpen, setAuthOpen] = useState(false);
  const [languageOpen, setLanguageOpen] = useState(false);
  const [loyaltyOpen, setLoyaltyOpen] = useState(false);
  const [loyaltyProgress, setLoyaltyProgress] = useState(0);
  const [loyaltyData, setLoyaltyData] = useState<{
    memberId?: string;
    pointsBalance?: number;
    status?: 'active' | 'inactive';
    tier?: 'silver' | 'gold' | 'platinum';
    isEnrolled?: boolean;
  }>({});
  useEffect(() => {
    const savedData = localStorage.getItem('loyaltyOnboarding');
    if (!savedData) return;
    const data = JSON.parse(savedData);
    const fields = [data.gender, data.maritalStatus, data.nationality, data.dateOfBirth, data.workingSector, data.city, data.district, data.preferredLanguage];
    const progress = Math.round(fields.filter(field => field !== '').length / fields.length * 100);
    setLoyaltyProgress(progress);
    setLoyaltyData(progress === 100 ? {
      memberId: '1234567890',
      pointsBalance: 3250,
      status: 'active',
      tier: 'gold',
      isEnrolled: true
    } : {
      isEnrolled: false
    });
  }, [loyaltyOpen]);
  const navigateTo = (path: string) => {
    navigate(path);
  };
  const handleAuthSuccess = (isNewUser: boolean) => {
    setAuthOpen(false);
    if (isNewUser && loyaltyProgress < 100) {
      setTimeout(() => setLoyaltyOpen(true), 350);
    }
  };
  const handleLoyaltyComplete = (data: Record<string, unknown>) => {
    localStorage.setItem('loyaltyOnboarding', JSON.stringify(data));
    setLoyaltyProgress(100);
    setLoyaltyData({
      memberId: '1234567890',
      pointsBalance: 0,
      status: 'active',
      tier: 'silver',
      isEnrolled: true
    });
    setLoyaltyOpen(false);
  };
  const shopCategories: Array<MenuDestination & {
    icon: ComponentType<{
      className?: string;
    }>;
  }> = [{
    label: 'Medicines (OTC)',
    path: '/category/medicines',
    icon: PillIcon
  }, {
    label: 'Vitamins & Supplements',
    path: '/category/vitamins-supplements',
    icon: ActivityIcon
  }, {
    label: 'Personal Care',
    path: '/category/personal-care',
    icon: SparklesIcon
  }, {
    label: 'Mother & Baby',
    path: '/category/mother-baby',
    icon: HeartIcon
  }];
  const conditions: Array<MenuDestination & {
    icon: ComponentType<{
      className?: string;
    }>;
  }> = [{
    label: 'Heart Health',
    path: '/category/heart-health',
    icon: HeartPulseIcon
  }, {
    label: 'Diabetes',
    path: '/category/diabetes',
    icon: ActivityIcon
  }, {
    label: 'Mental Health',
    path: '/category/mental-health',
    icon: BrainIcon
  }, {
    label: 'Pain Relief',
    path: '/category/pain-relief',
    icon: ThermometerIcon
  }, {
    label: 'Digestive Health',
    path: '/category/digestive-health',
    icon: PillIcon
  }, {
    label: 'Respiratory',
    path: '/category/respiratory',
    icon: StethoscopeIcon
  }];
  return <div className="min-h-screen bg-gray-50 md:hidden" data-id="element-7365" data-mp="1xtmjlobrjhjp">
      <header className="fixed inset-x-0 top-0 z-50 flex h-14 items-center border-b border-gray-100 bg-white px-4 safe-area-top" data-id="element-7366" data-mp="1xq8j6lmntiew">
        <button type="button" onClick={() => navigate(-1)} aria-label="Back to previous page" className="-ml-2 flex h-10 w-10 items-center justify-center rounded-full text-gray-700 active:bg-gray-100" data-id="element-7367" data-mp="26shpt0et4rn0">
          <ChevronLeftIcon className="h-6 w-6" data-id="element-7368" data-mp="2gl5k9oibejik" />
        </button>
        <h1 className="ml-2 flex-1 text-base font-bold text-gray-900" data-id="element-7369" data-mp="2bl16i68sk2w1">Menu</h1>
        <button type="button" onClick={() => navigate('/')} className="text-sm font-semibold text-[#1E2B55] active:opacity-70" data-id="element-7370" data-mp="2pj93uf9qp67q">
          Home
        </button>
      </header>

      <main className="mx-auto w-full max-w-lg pb-[calc(6rem+env(safe-area-inset-bottom))] pt-14" data-id="element-7371" data-mp="347j1egpilkgq">
        <section className="border-b border-gray-200 bg-white px-4 py-5" data-id="element-7372" data-mp="394htktpmbhca">
          <div className="flex items-center gap-3" data-id="element-7373" data-mp="2v6b2bwf6cfll">
            <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#1E2B55] text-white" data-id="element-7374" data-mp="2v5i4xm23v2g4">
              <UserIcon className="h-5 w-5" data-id="element-7375" data-mp="306z2b7lds6ry" />
            </div>
            <div className="flex-1" data-id="element-7376" data-mp="2lf6myvll6sq8">
              <p className="text-sm font-semibold text-gray-900" data-id="element-7377" data-mp="2le5biq0ns47z">
                Hello, Guest
              </p>
              <div className="mt-1 flex items-center gap-2 text-xs font-semibold text-[#1E2B55]" data-id="element-7378" data-mp="2cfa6vid9j26s">
                <button type="button" onClick={() => setAuthOpen(true)} className="min-h-8 active:opacity-70" data-id="element-7379" data-mp="2ggvf2qeuqsan">
                  Sign In
                </button>
                <span className="text-gray-300" aria-hidden="true" data-id="element-7380" data-mp="27ggtdpxz7b6m">
                  |
                </span>
                <button type="button" onClick={() => setAuthOpen(true)} className="min-h-8 active:opacity-70" data-id="element-7381" data-mp="d0s3wqk9ucqc">
                  Register
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-gray-200 bg-white px-4 py-3" data-id="element-7382" data-mp="3vf9cq30e4ec9">
          <button type="button" onClick={() => setLanguageOpen(true)} className="flex min-h-12 w-full items-center justify-between rounded-xl px-1 text-left active:bg-gray-50" data-id="element-7383" data-mp="vz4e8fmx5sni">
            <span className="flex items-center gap-3 text-sm font-medium text-gray-800" data-id="element-7384" data-mp="mwv25lc05r80">
              <GlobeIcon className="h-5 w-5 text-gray-500" data-id="element-7385" data-mp="hy0ea4z6hch1" />
              Language
            </span>
            <span className="flex items-center gap-1 rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-700" data-id="element-7386" data-mp="37p6oxz3idujg">
              {language === 'ar' ? 'Arabic' : 'English'}
              <ChevronRightIcon className="h-4 w-4" data-id="element-7387" data-mp="2sllyqbrdc9nq" />
            </span>
          </button>
        </section>

        <section className="border-b border-gray-200 bg-white p-4" data-id="element-7388" data-mp="2xjx8ynlksm6h">
          <LoyaltyCard progress={loyaltyProgress} loyaltyData={loyaltyData} onJoinNow={() => setLoyaltyOpen(true)} data-id="element-7389" data-mp="2xi46lkhzwo4a" />
        </section>

        <MenuSection title="Menu" data-id="element-7390" data-mp="37ccu1ts65qvl">
          <button type="button" onClick={() => navigateTo('/categories')} className="flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#1E2B55] px-4 py-3 text-sm font-bold text-white shadow-sm active:scale-[0.99]" data-id="element-7391" data-mp="3bc3piasn4v6g">
            <LayoutGridIcon className="h-4 w-4" data-id="element-7392" data-mp="3q9491r0ohjtu" />
            All Categories
          </button>
          <MenuRow icon={TrendingUpIcon} label="Best Sellers" badge="HOT" badgeClassName="bg-amber-500" onClick={() => navigateTo('/category/best-sellers')} data-id="element-7393" data-mp="3q6rttlmb1nkh" />
          <MenuRow icon={ShoppingBagIcon} label="New Arrivals" badge="NEW" badgeClassName="bg-[#1E2B55]" onClick={() => navigateTo('/category/new-arrivals')} data-id="element-7394" data-mp="1mpnwkisscg0" />
          <MenuRow icon={TagIcon} label="Deals & Offers" badge="SALE" badgeClassName="bg-red-500" onClick={() => navigateTo('/offers')} data-id="element-7395" data-mp="bbr86mwdlfzs" />
        </MenuSection>

        <MenuSection title="Shop by category" data-id="element-7396" data-mp="3evvlmofhasfs">
          {shopCategories.map(category => <MenuRow key={category.path} icon={category.icon} label={category.label} onClick={() => navigateTo(category.path)} showArrow data-id="element-7397" data-mp="39wsr3xx4k4m2" />)}
        </MenuSection>

        <MenuSection title="Browse by condition" data-id="element-7398" data-mp="133727209i501">
          <div className="grid grid-cols-2 gap-2" data-id="element-7399" data-mp="13631atnuizq2">
            {conditions.map(condition => {
            const Icon = condition.icon;
            return <button key={condition.path} type="button" onClick={() => navigateTo(condition.path)} className="flex min-h-16 items-center gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3 text-left text-xs font-semibold text-gray-700 active:bg-gray-100" data-id="element-7400" data-mp="16kkzcvpau5wj">
                  <Icon className="h-4 w-4 shrink-0 text-[#1E2B55]" data-id="element-7401" data-mp="1pcxcmdhhqrlg" />
                  <span data-id="element-7402" data-mp="1peqhpocgh3rc">{condition.label}</span>
                </button>;
          })}
          </div>
        </MenuSection>

        <MenuSection title="My account" data-id="element-7403" data-mp="1fndtd6pv8vq2">
          <MenuRow icon={UserIcon} label="My Account" onClick={() => navigateTo('/account')} showArrow data-id="element-7404" data-mp="1fmb04s52mh6z" />
          <MenuRow icon={PackageIcon} label="My Orders" onClick={() => navigateTo('/account/orders')} showArrow data-id="element-7405" data-mp="28b9ldy4hpzgi" />
          <MenuRow icon={HeartIcon} label="My Wishlist" onClick={() => navigateTo('/account/wishlist')} count={wishlist.length} showArrow data-id="element-7406" data-mp="2z8tmx2hdoh1q" />
        </MenuSection>

        <section className="border-b border-gray-200 bg-white px-4 py-3" data-id="element-7407" data-mp="2pbf6v3zd9u9y">
          <button type="button" onClick={() => navigate('/')} className="flex min-h-12 w-full items-center gap-3 rounded-xl px-1 text-sm font-semibold text-red-600 active:bg-red-50" data-id="element-7408" data-mp="2u9gpxowsltlf">
            <LogOutIcon className="h-5 w-5" data-id="element-7409" data-mp="2kckpm6h1sdry" />
            Sign Out
          </button>
        </section>
      </main>

      <MobileFooterNav data-id="element-7410" data-mp="26lpf6pqwhom9" />
      <AuthBottomSheet isOpen={authOpen} onClose={() => setAuthOpen(false)} onAuthSuccess={handleAuthSuccess} data-id="element-7411" data-mp="26i1mfsta568l" />
      <LanguageBottomSheet isOpen={languageOpen} onClose={() => setLanguageOpen(false)} currentLanguage={language} onLanguageChange={nextLanguage => {
      setLanguage(nextLanguage as 'en' | 'ar');
      setLanguageOpen(false);
    }} data-id="element-7412" data-mp="1wlorkrm00mc9" />
      <LoyaltyOnboardingBottomSheet isOpen={loyaltyOpen} onClose={() => setLoyaltyOpen(false)} onComplete={handleLoyaltyComplete} data-id="element-7413" data-mp="1dxxv6flay972" />
    </div>;
}
function MenuSection({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <section className="border-b border-gray-200 bg-white px-4 py-4" data-id="element-7414" data-mp="20y40bwbgrywh">
      <h2 className="mb-3 text-xs font-bold uppercase tracking-wide text-gray-500" data-id="element-7415" data-mp="20wape4ttisio">
        {title}
      </h2>
      <div className="space-y-1" data-id="element-7416" data-mp="1wupkb5nizij1">{children}</div>
    </section>;
}
function MenuRow({
  icon: Icon,
  label,
  onClick,
  badge,
  badgeClassName,
  count,
  showArrow = false
}: {
  icon: ComponentType<{
    className?: string;
  }>;
  label: string;
  onClick: () => void;
  badge?: string;
  badgeClassName?: string;
  count?: number;
  showArrow?: boolean;
}) {
  return <button type="button" onClick={onClick} className="flex min-h-12 w-full items-center gap-3 rounded-xl px-2 text-left text-sm font-medium text-gray-800 active:bg-gray-50" data-id="element-7417" data-mp="2fol0lccd4irs">
      <Icon className="h-5 w-5 shrink-0 text-[#1E2B55]" data-id="element-7418" data-mp="2rpufkits826n" />
      <span className="flex-1" data-id="element-7419" data-mp="2wnvz14v4e3in">{label}</span>
      {badge && <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold text-white ${badgeClassName}`} data-id="element-7420" data-mp="2wmb4vgcpwmlb">
          {badge}
        </span>}
      {count ? <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-[#1E2B55] px-1.5 text-[10px] font-bold text-white" data-id="element-7421" data-mp="36dxn3wnh3lxk">
          {count}
        </span> : null}
      {showArrow && <ChevronRightIcon className="h-5 w-5 text-gray-400" data-id="element-7422" data-mp="3fformy6a1mpu" />}
    </button>;
}